/**
 * Divisional Chakra Configuration
 * Universal config for D7, D8, D12, D20, D24, D30, D40, D60 chakras
 *
 * Architecture:
 * - Each divisional has configurable number of sectors
 * - Start point can be: sunrise, planets, or lagna
 * - Rulers optional (can be passed as array)
 */

/**
 * Available start points for chakra rendering
 */
export type ChakraStartPoint =
  | 'default'    // Fixed start point (e.g., 0° for Nakshatra)
  | 'sunrise'
  | 'surya'      // Sun
  | 'chandra'    // Moon
  | 'mangala'    // Mars
  | 'budha'      // Mercury
  | 'guru'       // Jupiter
  | 'shukra'     // Venus
  | 'shani'      // Saturn
  | 'rahu'       // North Node
  | 'ketu'       // South Node
  | 'lagna'      // Ascendant
  | 'custom';    // Custom degree

/**
 * Display info for start points
 */
export const START_POINT_INFO: Record<ChakraStartPoint, { label: string; shortCode: string; color: string }> = {
  default: { label: 'Default (0°)', shortCode: 'DF', color: '#718096' },
  sunrise: { label: 'Sunrise', shortCode: 'SR', color: '#FFA500' },
  surya: { label: 'Surya', shortCode: 'Sy', color: '#FF6B6B' },
  chandra: { label: 'Chandra', shortCode: 'Ch', color: '#E8E8E8' },
  mangala: { label: 'Mangala', shortCode: 'Ma', color: '#DC143C' },
  budha: { label: 'Budha', shortCode: 'Bu', color: '#32CD32' },
  guru: { label: 'Guru', shortCode: 'Gu', color: '#FFD700' },
  shukra: { label: 'Shukra', shortCode: 'Sk', color: '#FF69B4' },
  shani: { label: 'Shani', shortCode: 'Sa', color: '#000080' },
  rahu: { label: 'Rahu', shortCode: 'Ra', color: '#8B4513' },
  ketu: { label: 'Ketu', shortCode: 'Ke', color: '#696969' },
  lagna: { label: 'Lagna', shortCode: 'La', color: '#9370DB' },
  custom: { label: 'Custom', shortCode: 'CU', color: '#3182CE' }
};

/**
 * Custom start point settings
 */
export interface CustomStartPointSettings {
  degrees: number;  // 0-360
  label: string;    // e.g., "L1"
}

/**
 * Divisional chakra type
 */
export type DivisionalChakraType = 'muhurta' | 'nakshatra' | 'd7' | 'd8' | 'd12' | 'd20' | 'd24' | 'd30' | 'd40' | 'd60';

/**
 * Configuration for each divisional chakra
 */
export interface DivisionalChakraConfig {
  type: DivisionalChakraType;
  name: string;
  vedicName: string;
  sectors: number;
  degreesPerSector: number;
  defaultVisible: boolean;
  defaultStartPoint: ChakraStartPoint;
  colorScheme: string; // Chakra UI color scheme
  description: string;
  hasSubdivisions?: boolean; // For pada, etc.
  subdivisions?: {
    name: string;
    count: number; // Number of subdivisions per sector
    degreesPerSubdivision: number;
  };
}

/**
 * All divisional chakra configurations
 */
export const DIVISIONAL_CHAKRA_CONFIGS: Record<DivisionalChakraType, DivisionalChakraConfig> = {
  muhurta: {
    type: 'muhurta',
    name: 'Muhurta',
    vedicName: 'Muhurta',
    sectors: 30,
    degreesPerSector: 12,
    defaultVisible: false,
    defaultStartPoint: 'sunrise',
    colorScheme: 'orange',
    description: 'Muhurta Chakra - 30 sectors of 12° each'
  },
  nakshatra: {
    type: 'nakshatra',
    name: 'Nakshatra',
    vedicName: 'Nakshatra Chakra',
    sectors: 27,
    degreesPerSector: 360 / 27, // 13.333°
    defaultVisible: true, // Nakshatra enabled by default
    defaultStartPoint: 'default', // Fixed at 0° Ashwini
    colorScheme: 'purple',
    description: 'Nakshatra Chakra - 27 lunar mansions',
    hasSubdivisions: true,
    subdivisions: {
      name: 'pada',
      count: 4, // 4 padas per nakshatra
      degreesPerSubdivision: 360 / (27 * 4) // 3.333°
    }
  },
  d7: {
    type: 'd7',
    name: 'D7 Saptamsa',
    vedicName: 'Saptamsa',
    sectors: 7,
    degreesPerSector: 360 / 7, // ~51.43°
    defaultVisible: false,
    defaultStartPoint: 'sunrise',
    colorScheme: 'cyan',
    description: 'Children and progeny'
  },
  d8: {
    type: 'd8',
    name: 'D8 Ashtamsha',
    vedicName: 'Ashtamsha Vara',
    sectors: 8,
    degreesPerSector: 45, // 360 / 8
    defaultVisible: false,
    defaultStartPoint: 'sunrise',
    colorScheme: 'orange',
    description: 'Eight-fold divisions of day'
  },
  d12: {
    type: 'd12',
    name: 'D12 Dwadasamsa',
    vedicName: 'Dwadasamsa',
    sectors: 12,
    degreesPerSector: 30, // 360 / 12
    defaultVisible: false,
    defaultStartPoint: 'sunrise',
    colorScheme: 'yellow',
    description: 'Parents and lineage'
  },
  d20: {
    type: 'd20',
    name: 'D20 Vimsamsa',
    vedicName: 'Vimsamsa',
    sectors: 20,
    degreesPerSector: 18, // 360 / 20
    defaultVisible: false,
    defaultStartPoint: 'sunrise',
    colorScheme: 'green',
    description: 'Spiritual pursuits'
  },
  d24: {
    type: 'd24',
    name: 'D24 Siddhamsa',
    vedicName: 'Chaturvimsamsa',
    sectors: 24,
    degreesPerSector: 15, // 360 / 24
    defaultVisible: false,
    defaultStartPoint: 'sunrise',
    colorScheme: 'blue',
    description: 'Education and learning'
  },
  d30: {
    type: 'd30',
    name: 'D30 Trimsamsa',
    vedicName: 'Trimsamsa',
    sectors: 30,
    degreesPerSector: 12, // 360 / 30
    defaultVisible: false,
    defaultStartPoint: 'sunrise',
    colorScheme: 'purple',
    description: 'Evils and misfortunes'
  },
  d40: {
    type: 'd40',
    name: 'D40 Khavedamsa',
    vedicName: 'Khavedamsa',
    sectors: 40,
    degreesPerSector: 9, // 360 / 40
    defaultVisible: false,
    defaultStartPoint: 'sunrise',
    colorScheme: 'pink',
    description: 'Auspicious and inauspicious effects'
  },
  d60: {
    type: 'd60',
    name: 'D60 Shashtiamsa',
    vedicName: 'Shashtiamsa',
    sectors: 60,
    degreesPerSector: 6, // 360 / 60
    defaultVisible: false,
    defaultStartPoint: 'sunrise',
    colorScheme: 'red',
    description: 'Past karmas'
  }
};

/**
 * Get config for a divisional chakra
 */
export function getDivisionalChakraConfig(type: DivisionalChakraType): DivisionalChakraConfig {
  return DIVISIONAL_CHAKRA_CONFIGS[type];
}

/**
 * Get all start point options in display order
 */
export function getStartPointOptions(): ChakraStartPoint[] {
  return [
    'default',
    'sunrise',
    'lagna',
    'surya',
    'chandra',
    'mangala',
    'budha',
    'guru',
    'shukra',
    'shani',
    'rahu',
    'ketu',
    'custom'
  ];
}

/**
 * Default settings for all divisional chakras
 */
export const DIVISIONAL_CHAKRA_DEFAULTS = {
  defaultVisible: false,
  defaultStartPoint: 'sunrise' as ChakraStartPoint,
  enableAnimations: true,
  showSectorNumbers: true,
  showRulerCodes: false, // Off by default, can be enabled per-chakra
  transitionDuration: 300
} as const;
